import type { MergeFieldGroup } from '@platform/shared-types';

/**
 * Colour for the merge-field chips (editor + palette). Deliberately NOT in
 * `@platform/shared-types` — that package is framework-agnostic and runs in
 * Lambdas, so it has no Tailwind classes or colour values, only `group`.
 *
 * `MergeFieldGroup` is imported `type`-only: it is erased at compile time, so
 * a stale Vite pre-bundle of the RUNTIME exports (the catalog array, the
 * lookup functions) can never make this import `undefined` — nothing here
 * needs guarding on that account. Runtime catalog access is guarded where it
 * happens, in `MergeTextEditor.tsx` and `MergeFieldPalette.tsx`.
 *
 * See the PALETTE RULE in `./ui.tsx`: only Tailwind's DEFAULT colour names
 * survive in both consuming apps (super-admin-ui's tailwind.config.js and
 * platform-app's CDN Tailwind) — no `dark-*` or `admin-*` custom tokens here.
 */

interface MergeFieldChipPalette {
  /** Rest-state chip classes: background, text and border. */
  chip: string;
  /** Applied in addition to `chip` while the chip is the drag source. */
  dragging: string;
  /** The small dot used next to a group heading in the palette. */
  dot: string;
}

const GROUP_PALETTES: Record<MergeFieldGroup, MergeFieldChipPalette> = {
  company: {
    chip: 'border border-blue-500/30 bg-blue-500/15 text-blue-700',
    dragging: 'border-blue-500/60 bg-blue-500/25',
    dot: 'bg-blue-500',
  },
  customer: {
    chip: 'border border-emerald-500/30 bg-emerald-500/15 text-emerald-700',
    dragging: 'border-emerald-500/60 bg-emerald-500/25',
    dot: 'bg-emerald-500',
  },
  signer: {
    chip: 'border border-violet-500/30 bg-violet-500/15 text-violet-700',
    dragging: 'border-violet-500/60 bg-violet-500/25',
    dot: 'bg-violet-500',
  },
  guarantor: {
    chip: 'border border-amber-500/30 bg-amber-500/15 text-amber-700',
    dragging: 'border-amber-500/60 bg-amber-500/25',
    dot: 'bg-amber-500',
  },
  equipment: {
    chip: 'border border-cyan-500/30 bg-cyan-500/15 text-cyan-700',
    dragging: 'border-cyan-500/60 bg-cyan-500/25',
    dot: 'bg-cyan-500',
  },
  // The specific device, distinguished from `equipment`'s roll-up by shade
  // rather than hue — they are the same subject at two levels of detail, and
  // an unrelated colour would read as an unrelated thing.
  device: {
    chip: 'border border-teal-500/30 bg-teal-500/15 text-teal-700',
    dragging: 'border-teal-500/60 bg-teal-500/25',
    dot: 'bg-teal-500',
  },
  package: {
    chip: 'border border-indigo-500/30 bg-indigo-500/15 text-indigo-700',
    dragging: 'border-indigo-500/60 bg-indigo-500/25',
    dot: 'bg-indigo-500',
  },
  finance: {
    chip: 'border border-rose-500/30 bg-rose-500/15 text-rose-700',
    dragging: 'border-rose-500/60 bg-rose-500/25',
    dot: 'bg-rose-500',
  },
  vendor: {
    chip: 'border border-orange-500/30 bg-orange-500/15 text-orange-700',
    dragging: 'border-orange-500/60 bg-orange-500/25',
    dot: 'bg-orange-500',
  },
  employee: {
    chip: 'border border-lime-600/30 bg-lime-600/15 text-lime-700',
    dragging: 'border-lime-600/60 bg-lime-600/25',
    dot: 'bg-lime-600',
  },
  partner: {
    chip: 'border border-fuchsia-500/30 bg-fuchsia-500/15 text-fuchsia-700',
    dragging: 'border-fuchsia-500/60 bg-fuchsia-500/25',
    dot: 'bg-fuchsia-500',
  },
  agreement: {
    chip: 'border border-slate-500/30 bg-slate-500/15 text-slate-700',
    dragging: 'border-slate-500/60 bg-slate-500/25',
    dot: 'bg-slate-500',
  },
};

/** Danger style for a token that is not (or no longer) in the catalog — it
 *  will throw `MissingMergeFieldError` at render time, so it must read as
 *  broken while the author is still typing, not just after they save. */
export const DANGER_CHIP_CLASSES = 'border border-red-500/40 bg-red-500/15 text-red-700';
export const DANGER_CHIP_DRAGGING_CLASSES = 'border-red-500/70 bg-red-500/25';

/** Chip classes (rest state) for a known field's group. */
export function chipClassesForGroup(group: MergeFieldGroup): string {
  return GROUP_PALETTES[group]?.chip ?? DANGER_CHIP_CLASSES;
}

/** Chip classes while it is the drag source, for a known field's group. */
export function chipDraggingClassesForGroup(group: MergeFieldGroup): string {
  return GROUP_PALETTES[group]?.dragging ?? DANGER_CHIP_DRAGGING_CLASSES;
}

/** The small coloured dot shown next to a group heading in the palette. */
export function dotClassForGroup(group: MergeFieldGroup): string {
  return GROUP_PALETTES[group]?.dot ?? 'bg-red-500';
}
